import { useMemo, useRef, useState } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import { restrictToVerticalAxis, restrictToParentElement } from '@dnd-kit/modifiers';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useProjectContext } from '../contexts/ProjectContext';
import {
  useTasks,
  useCreateTask,
  useUpdateTask,
  useDeleteTask,
  useAllegaFile,
  useRiordinaTasks,
} from '../hooks/useTasks';
import { downloadDocumento } from '../hooks/useDocumenti';
import { prepareFileForUpload, formatSize } from '../utils/imageResize';
import { analizzaRighe, contaChecklist, toggleRiga } from '../utils/checklist';
import type { Task as TaskType } from '../types';

const CATEGORIE = [
  'sopralluogo',
  'verifiche',
  'tetto',
  'serramenti',
  'impianti',
  'attrezzi',
  'burocrazia',
  'altro',
];

export function Task() {
  const { currentProject } = useProjectContext();
  const projectId: string | undefined = currentProject?.id;

  const { data: tasks = [], isLoading } = useTasks(projectId);
  const createTask = useCreateTask();
  const updateTask = useUpdateTask();
  const deleteTask = useDeleteTask();
  const riordinaTasks = useRiordinaTasks();

  // Il puntatore parte solo dopo qualche pixel, il dito dopo una pressione breve:
  // così restano vivi i click sulla riga e lo scroll della pagina da mobile.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const [nuovoTitolo, setNuovoTitolo] = useState('');
  const [nuovaCategoria, setNuovaCategoria] = useState('altro');
  const [errore, setErrore] = useState<string | null>(null);

  const { daFare, completate } = useMemo(
    () => ({
      daFare: tasks.filter((t) => !t.completata),
      completate: tasks.filter((t) => t.completata),
    }),
    [tasks]
  );

  if (!currentProject) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Seleziona un progetto per vedere i task</p>
      </div>
    );
  }

  async function aggiungi(e: React.FormEvent) {
    e.preventDefault();
    setErrore(null);
    if (!nuovoTitolo.trim() || !projectId) return;

    try {
      await createTask.mutateAsync({
        projectId,
        titolo: nuovoTitolo.trim(),
        categoria: nuovaCategoria,
      });
      setNuovoTitolo('');
    } catch {
      setErrore('Non sono riuscito a salvare il task. Riprova.');
    }
  }

  function fineTrascinamento(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id || !projectId) return;

    const da = daFare.findIndex((t) => t.id === active.id);
    const a = daFare.findIndex((t) => t.id === over.id);
    if (da === -1 || a === -1) return;

    const ids = arrayMove(daFare, da, a).map((t) => t.id);
    riordinaTasks.mutate({ projectId, ids });
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Task</h2>
        <p className="text-gray-600 mt-1">
          Cose da fare che escono parlando. Spunta quando è fatto, annota misure e allega foto.
        </p>
      </div>

      {/* Aggiunta rapida */}
      <form onSubmit={aggiungi} className="bg-white rounded-lg shadow p-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <input
            type="text"
            value={nuovoTitolo}
            onChange={(e) => setNuovoTitolo(e.target.value)}
            placeholder="Cosa c'è da fare?"
            className="flex-1"
            maxLength={300}
            aria-label="Nuovo task"
          />
          <select
            value={nuovaCategoria}
            onChange={(e) => setNuovaCategoria(e.target.value)}
            className="sm:w-44"
            aria-label="Categoria"
          >
            {CATEGORIE.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
          <button
            type="submit"
            className="btn-primary whitespace-nowrap"
            disabled={createTask.isPending || !nuovoTitolo.trim()}
          >
            {createTask.isPending ? 'Aggiungo…' : 'Aggiungi'}
          </button>
        </div>
        {errore && <p className="text-sm text-red-600 mt-2">{errore}</p>}
      </form>

      {isLoading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
        </div>
      ) : tasks.length === 0 ? (
        <div className="bg-gray-50 rounded-xl p-8 text-center">
          <div className="text-5xl mb-3">✅</div>
          <h3 className="text-lg font-medium text-gray-900">Nessun task</h3>
          <p className="mt-2 text-sm text-gray-500">
            Aggiungine uno qui sopra, oppure ne compariranno man mano che ne parliamo.
          </p>
        </div>
      ) : (
        <>
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-gray-900">Da fare</h3>
                {daFare.length > 1 && (
                  <p className="text-xs text-gray-500 mt-0.5">
                    Trascina ⠿ per cambiare l&apos;ordine
                  </p>
                )}
              </div>
              <span className="text-sm text-gray-500">{daFare.length}</span>
            </div>
            {daFare.length === 0 ? (
              <p className="px-6 py-6 text-sm text-gray-500">Tutto fatto. 🎉</p>
            ) : (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                modifiers={[restrictToVerticalAxis, restrictToParentElement]}
                onDragEnd={fineTrascinamento}
              >
                <SortableContext
                  items={daFare.map((t) => t.id)}
                  strategy={verticalListSortingStrategy}
                >
                  <div className="divide-y divide-gray-100">
                    {daFare.map((t) => (
                      <RigaTask
                        key={t.id}
                        task={t}
                        projectId={currentProject.id}
                        riordinabile
                        onToggle={(task) =>
                          updateTask.mutate({
                            id: task.id,
                            projectId: currentProject.id,
                            data: { completata: !task.completata },
                          })
                        }
                        onSaveNote={(task, note) =>
                          updateTask.mutateAsync({
                            id: task.id,
                            projectId: currentProject.id,
                            data: { note },
                          })
                        }
                        onSaveIstruzioni={(task, istruzioni) =>
                          updateTask.mutateAsync({
                            id: task.id,
                            projectId: currentProject.id,
                            data: { istruzioni },
                          })
                        }
                        onDelete={(task) => {
                          if (window.confirm(`Eliminare "${task.titolo}"?`)) {
                            deleteTask.mutate({ id: task.id, projectId: currentProject.id });
                          }
                        }}
                      />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
            )}
          </div>

          {completate.length > 0 && (
            <details className="bg-white rounded-lg shadow overflow-hidden">
              <summary className="px-6 py-4 cursor-pointer font-semibold text-gray-700">
                Completate ({completate.length})
              </summary>
              <div className="divide-y divide-gray-100 border-t border-gray-200">
                {completate.map((t) => (
                  <RigaTask
                    key={t.id}
                    task={t}
                    projectId={currentProject.id}
                    onToggle={(task) =>
                      updateTask.mutate({
                        id: task.id,
                        projectId: currentProject.id,
                        data: { completata: !task.completata },
                      })
                    }
                    onSaveNote={(task, note) =>
                      updateTask.mutateAsync({
                        id: task.id,
                        projectId: currentProject.id,
                        data: { note },
                      })
                    }
                    onSaveIstruzioni={(task, istruzioni) =>
                      updateTask.mutateAsync({
                        id: task.id,
                        projectId: currentProject.id,
                        data: { istruzioni },
                      })
                    }
                    onDelete={(task) => {
                      if (window.confirm(`Eliminare "${task.titolo}"?`)) {
                        deleteTask.mutate({ id: task.id, projectId: currentProject.id });
                      }
                    }}
                  />
                ))}
              </div>
            </details>
          )}
        </>
      )}
    </div>
  );
}

interface RigaTaskProps {
  task: TaskType;
  projectId: string;
  /** Mostra la maniglia di trascinamento: solo nella lista "Da fare". */
  riordinabile?: boolean;
  onToggle: (t: TaskType) => void;
  onSaveNote: (t: TaskType, note: string | null) => Promise<unknown>;
  onSaveIstruzioni: (t: TaskType, istruzioni: string) => Promise<unknown>;
  onDelete: (t: TaskType) => void;
}

function RigaTask({
  task,
  projectId,
  riordinabile = false,
  onToggle,
  onSaveNote,
  onSaveIstruzioni,
  onDelete,
}: RigaTaskProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: task.id,
    disabled: !riordinabile,
  });
  const [aperto, setAperto] = useState(false);
  const [note, setNote] = useState(task.note ?? '');
  const [istruzioni, setIstruzioni] = useState(task.istruzioni ?? '');
  // Le note con checkbox si mostrano spuntabili; la matita riporta alla textarea.
  const [modificaNote, setModificaNote] = useState(false);
  const [salvato, setSalvato] = useState(false);
  const [caricamento, setCaricamento] = useState<string | null>(null);
  const inputFile = useRef<HTMLInputElement>(null);
  const allegaFile = useAllegaFile();

  const allegati = task.allegati ?? [];

  const noteHaChecklist = contaChecklist(note).totali > 0;
  const contaVoci = useMemo(() => {
    const i = contaChecklist(istruzioni);
    const n = contaChecklist(note);
    return { fatte: i.fatte + n.fatte, totali: i.totali + n.totali };
  }, [istruzioni, note]);

  function toggleVoceIstruzioni(indice: number) {
    const prima = istruzioni;
    const nuovo = toggleRiga(prima, indice);
    if (nuovo === prima) return;
    setIstruzioni(nuovo);
    onSaveIstruzioni(task, nuovo).catch(() => setIstruzioni(prima));
  }

  function toggleVoceNote(indice: number) {
    const prima = note;
    const nuovo = toggleRiga(prima, indice);
    if (nuovo === prima) return;
    setNote(nuovo);
    onSaveNote(task, nuovo).catch(() => setNote(prima));
  }

  const scaduta =
    !!task.scadenza &&
    !task.completata &&
    new Date(task.scadenza).setHours(0, 0, 0, 0) < new Date().setHours(0, 0, 0, 0);

  async function salvaNote() {
    const valore = note.trim();
    if (valore === (task.note ?? '')) return;
    await onSaveNote(task, valore || null);
    setSalvato(true);
    setTimeout(() => setSalvato(false), 2000);
  }

  async function onFileScelti(e: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files ?? []);
    if (files.length === 0) return;

    for (const file of files) {
      setCaricamento(`${file.name}…`);
      try {
        const payload = await prepareFileForUpload(file);
        await allegaFile.mutateAsync({
          projectId,
          taskId: task.id,
          nome: file.name,
          file: { data: payload.data, mediaType: payload.mediaType, filename: payload.filename },
        });
      } catch {
        setCaricamento(`Errore su ${file.name}`);
        await new Promise((r) => setTimeout(r, 2000));
      }
    }

    setCaricamento(null);
    if (inputFile.current) inputFile.current.value = '';
  }

  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        zIndex: isDragging ? 10 : undefined,
      }}
      className={[
        task.completata ? 'bg-gray-50/60' : 'bg-white',
        isDragging ? 'relative shadow-lg ring-1 ring-primary-200 rounded-lg' : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <div className="flex items-start gap-2 sm:gap-3 px-2 sm:px-4 py-3">
        {riordinabile && (
          <button
            type="button"
            {...attributes}
            {...listeners}
            className="mt-0.5 shrink-0 px-1.5 py-1 text-gray-400 hover:text-gray-600 cursor-grab active:cursor-grabbing touch-none"
            aria-label={`Sposta: ${task.titolo}`}
            title="Trascina per riordinare"
          >
            ⠿
          </button>
        )}
        <input
          type="checkbox"
          checked={task.completata}
          onChange={() => onToggle(task)}
          className="mt-1 shrink-0 w-5 h-5"
          aria-label={`Segna come completato: ${task.titolo}`}
        />

        <button
          onClick={() => setAperto((v) => !v)}
          className="flex-1 text-left min-w-0"
          aria-expanded={aperto}
        >
          <p
            className={`text-sm font-medium ${
              task.completata ? 'text-gray-400 line-through' : 'text-gray-900'
            }`}
          >
            {task.titolo}
          </p>
          <p className="text-xs text-gray-500 mt-0.5">
            {task.categoria && <span className="mr-2">{task.categoria}</span>}
            {task.scadenza && !task.completata && (
              <span className={`mr-2 ${scaduta ? 'text-red-600 font-medium' : ''}`}>
                entro {new Date(task.scadenza).toLocaleDateString('it-IT')}
              </span>
            )}
            {allegati.length > 0 && <span className="mr-2">📎 {allegati.length}</span>}
            {contaVoci.totali > 0 && (
              <span
                className={`mr-2 ${
                  contaVoci.fatte === contaVoci.totali ? 'text-green-600' : ''
                }`}
              >
                ☑ {contaVoci.fatte}/{contaVoci.totali}
              </span>
            )}
            {task.istruzioni && !aperto && <span className="mr-2">ℹ️</span>}
            {task.note && !aperto && <span className="mr-2">📝</span>}
            {task.completataIl && (
              <span>
                fatto il {new Date(task.completataIl).toLocaleDateString('it-IT')}
              </span>
            )}
          </p>
          {task.ganttTask && !task.completata && (
            <p className="text-xs mt-1 text-amber-700">
              ⛓ blocca «{task.ganttTask.nome}» — dal{' '}
              {new Date(task.ganttTask.dataInizio).toLocaleDateString('it-IT')}
            </p>
          )}
        </button>

        <div className="flex items-center gap-1 shrink-0">
          <button
            onClick={() => setAperto((v) => !v)}
            className="text-sm text-primary-600 hover:text-primary-700 px-1"
          >
            {aperto ? 'Chiudi' : 'Apri'}
          </button>
          <button
            onClick={() => onDelete(task)}
            className="p-1.5 text-gray-400 hover:text-red-600 rounded"
            aria-label={`Elimina: ${task.titolo}`}
            title="Elimina task"
          >
            🗑
          </button>
        </div>
      </div>

      {aperto && (
        <div className="px-4 sm:px-6 pb-4 space-y-3">
          {task.istruzioni && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-xs font-semibold text-blue-900 uppercase tracking-wide mb-2">
                Istruzioni
              </p>
              {contaChecklist(istruzioni).totali > 0 ? (
                <TestoConChecklist
                  testo={istruzioni}
                  className="text-sm text-blue-900 leading-relaxed"
                  onToggleVoce={toggleVoceIstruzioni}
                />
              ) : (
                <p className="text-sm text-blue-900 whitespace-pre-wrap leading-relaxed">
                  {task.istruzioni}
                </p>
              )}
            </div>
          )}

          <div>
            <label className="input-label" htmlFor={`note-${task.id}`}>
              Le tue note
            </label>
            {noteHaChecklist && !modificaNote ? (
              <div className="border border-gray-200 rounded-lg px-3 py-2 bg-white">
                <TestoConChecklist
                  testo={note}
                  className="text-sm text-gray-800 leading-relaxed"
                  onToggleVoce={toggleVoceNote}
                />
                <p className="text-xs text-gray-400 mt-2">
                  <button
                    type="button"
                    onClick={() => setModificaNote(true)}
                    className="text-primary-600 hover:text-primary-700"
                  >
                    ✏️ Modifica il testo
                  </button>
                  <span className="ml-2">le spunte si salvano da sole</span>
                </p>
              </div>
            ) : (
              <>
                <textarea
                  id={`note-${task.id}`}
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  onBlur={() => {
                    setModificaNote(false);
                    void salvaNote();
                  }}
                  rows={6}
                  className="w-full font-mono text-sm"
                  placeholder={
                    'Camera 1 — vano 84,5 × 142 (min 83,8)\nCamera 2 — vano 80,2 × 140,5\n…'
                  }
                  maxLength={5000}
                />
                <p className="text-xs text-gray-400 mt-1">
                  {salvato ? '✅ salvato' : 'si salva quando esci dal campo'}
                </p>
              </>
            )}
          </div>

          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <input
                ref={inputFile}
                type="file"
                accept="image/*,application/pdf"
                multiple
                onChange={onFileScelti}
                className="hidden"
                id={`file-${task.id}`}
              />
              <label htmlFor={`file-${task.id}`} className="btn-secondary cursor-pointer text-sm">
                📷 Allega foto
              </label>
              {caricamento && <span className="text-sm text-gray-500">{caricamento}</span>}
            </div>

            {allegati.length > 0 && (
              <ul className="mt-3 space-y-1">
                {allegati.map((a) => (
                  <li key={a.id} className="flex items-center justify-between gap-3 text-sm">
                    <span className="truncate text-gray-700">{a.nome}</span>
                    <div className="flex items-center gap-3 shrink-0">
                      <span className="text-xs text-gray-400">{formatSize(a.size)}</span>
                      <button
                        onClick={() => downloadDocumento(a.id, a.originalName)}
                        className="text-primary-600 hover:text-primary-700"
                      >
                        Scarica
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

        </div>
      )}
    </div>
  );
}

interface TestoConChecklistProps {
  testo: string;
  className?: string;
  onToggleVoce: (indice: number) => void;
}

/**
 * Testo con le righe `- [ ]` / `- [x]` rese come checkbox cliccabili (#47).
 * Le altre righe restano testo com'erano, whitespace preservato.
 */
function TestoConChecklist({ testo, className, onToggleVoce }: TestoConChecklistProps) {
  const righe = analizzaRighe(testo);
  return (
    <div className={className}>
      {righe.map((riga) =>
        riga.tipo === 'checkbox' ? (
          <label
            key={riga.indice}
            className="flex items-start gap-2 py-0.5 cursor-pointer"
            style={riga.indent ? { paddingLeft: `${riga.indent.length}ch` } : undefined}
          >
            <input
              type="checkbox"
              checked={riga.spuntata}
              onChange={() => onToggleVoce(riga.indice)}
              className="mt-0.5 shrink-0 w-4 h-4"
            />
            <span className={riga.spuntata ? 'line-through opacity-50' : ''}>{riga.testo}</span>
          </label>
        ) : (
          <div key={riga.indice} className="whitespace-pre-wrap">
            {riga.testo === '' ? ' ' : riga.testo}
          </div>
        )
      )}
    </div>
  );
}
